import { currentYear } from "@/lib/siteYear";
import { useParams, Link } from "@/lib/router-compat";
import { jsonLd } from "@/lib/seoHelpers";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronRight, Star, ExternalLink, ShoppingCart, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { buyersGuides, type BuyersGuideSection } from "@/lib/buyersGuideConfig";
import { BreadcrumbSchema } from "@/components/SEO/BreadcrumbSchema";
import { cn } from "@/lib/utils";
import NotFound from "./NotFound";

const BASE_URL = "https://www.garden-review.co.uk";
const Y = currentYear();

interface AmazonItem {
  asin: string;
  title: string;
  brand?: string | null;
  image?: string | null;
  price_display?: string | null;
  rating?: number | null;
  review_count?: number | null;
  detail_url: string;
}

const AmazonBuyersGuide = () => {
  const { slug: rawSlug } = useParams();
  // The route delivers the full "best-<key>-uk" segment; extract the guide key.
  const slug = rawSlug?.match(/^best-(.+)-uk$/)?.[1] ?? rawSlug;
  const config = slug ? buyersGuides[slug] : null;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["buyers-guide", slug],
    enabled: !!config && !config.sections?.length,
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-comparison", {
        body: {
          keywords: config!.amazonKeywords,
          searchIndex: config!.searchIndex || "All",
          itemCount: 10,
          sortBy: "AvgCustomerReviews",
          skipAi: true,
        },
      });
      if (error) throw error;
      return (data?.items || []) as AmazonItem[];
    },
  });

  const sections = config?.sections ?? [];
  const { data: sectionItems = {}, isLoading: sectionsLoading } = useQuery({
    queryKey: ["buyers-guide-sections", slug],
    enabled: sections.length > 0,
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const out: Record<string, AmazonItem[]> = {};
      const seen = new Set<string>();
      const invoke = async (body: Record<string, unknown>) => {
        let res = await supabase.functions.invoke("generate-comparison", { body });
        if (res.error) {
          // Amazon throttles rapid back-to-back requests; one spaced retry.
          await new Promise((r) => setTimeout(r, 2500));
          res = await supabase.functions.invoke("generate-comparison", { body });
        }
        return res.error ? [] : ((res.data?.items || []) as AmazonItem[]);
      };
      for (const sec of sections) {
        let pinned: AmazonItem[] = [];
        if (sec.pinnedAsins?.length) {
          const got = await invoke({ asins: sec.pinnedAsins, itemCount: 10, skipAi: true });
          // keep the configured order
          pinned = sec.pinnedAsins.map((a) => got.find((it) => it.asin === a)).filter(Boolean) as AmazonItem[];
          pinned.forEach((it) => seen.add(it.asin));
        }
        if (sec.pinnedOnly) { out[sec.id] = pinned; continue; }
        await new Promise((r) => setTimeout(r, 1000));
        const searched = await invoke({ keywords: sec.amazonKeywords, searchIndex: "All", itemCount: 10, sortBy: "AvgCustomerReviews", skipAi: true });
        const list = searched.filter((it) => {
          if (seen.has(it.asin) || !isRelevant(it.title, sec)) return false;
          seen.add(it.asin);
          return true;
        });
        out[sec.id] = [...list, ...pinned].slice(0, 6);
      }
      return out;
    },
  });

  const reviewSlugs = Array.from(new Set(sections.flatMap((s) => Object.values(s.reviews ?? {}))));
  const { data: publishedReviews = [] } = useQuery({
    queryKey: ["buyers-guide-reviews", reviewSlugs],
    enabled: reviewSlugs.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("articles").select("slug").eq("status", "published").in("slug", reviewSlugs);
      return (data ?? []).map((r) => r.slug);
    },
  });
  const reviewFor = (sec: BuyersGuideSection, asin: string) => {
    const s = sec.reviews?.[asin];
    return s && publishedReviews.includes(s) ? s : undefined;
  };

  if (!config) return <NotFound />;

  const canonical = `${BASE_URL}/best-${config.slug}-uk`;
  const metaTitle = `Best ${config.title} UK ${Y} | Top-Rated Picks & Buyer's Guide`;
  const metaDesc = `Looking for the best ${config.singular} in the UK? Live top-rated picks from Amazon plus our expert buyer's guide for ${Y}.`;

  const faqJsonLd = config.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: config.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const listItems = sections.length ? sections.flatMap((s) => sectionItems[s.id] ?? []) : items;
  const itemListJsonLd = listItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `Best ${config.title} UK ${Y}`,
        itemListElement: listItems.slice(0, 18).map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: it.title,
          url: it.detail_url,
        })),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        {faqJsonLd && <script type="application/ld+json">{jsonLd(faqJsonLd)}</script>}
        {itemListJsonLd && <script type="application/ld+json">{jsonLd(itemListJsonLd)}</script>}
      </Helmet>
      <BreadcrumbSchema
        items={[
          { name: "Home", url: BASE_URL },
          { name: "Reviews", url: `${BASE_URL}/category/reviews` },
          { name: config.title, url: canonical },
        ]}
      />

      <div className="min-h-screen bg-background">
        <Header />

        <section className={cn("border-b", config.heroGradient)}>
          <div className="container mx-auto px-4 py-10 md:py-14 max-w-5xl">
            <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to="/category/reviews" className="hover:text-foreground">Reviews</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-foreground font-medium">Best {config.title}</span>
            </nav>
            <Badge variant="secondary" className="mb-3 gap-1">
              <Sparkles className="h-3 w-3" /> Updated {Y} · Live Amazon picks
            </Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Best {config.title} UK {Y}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl">{config.intro}</p>
            <p className="text-xs text-muted-foreground mt-4">
              As an Amazon Associate we earn from qualifying purchases. Prices and ratings refresh automatically.
            </p>
          </div>
        </section>

        <main className="container mx-auto px-4 py-10 max-w-5xl">
          {sections.length > 0 ? (
            <>
              <nav aria-label="Jump to type" className="flex flex-wrap gap-2 mb-10">
                {sections.map((sec) => (
                  <a key={sec.id} href={`#${sec.id}`} className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium">
                    {sec.title}
                  </a>
                ))}
              </nav>
              {sections.map((sec) => (
                <section key={sec.id} id={sec.id} className="mb-14 scroll-mt-24">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">Best {sec.title} UK</h2>
                  <p className="text-muted-foreground mb-6 max-w-3xl">{sec.intro}</p>
                  <ProductGrid items={sectionItems[sec.id] ?? []} isLoading={sectionsLoading} reviewFor={(asin) => reviewFor(sec, asin)} />
                </section>
              ))}
            </>
          ) : (
            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Top-rated {config.title} on Amazon UK</h2>
              <ProductGrid items={items} isLoading={isLoading} />
            </section>
          )}

          {/* Buyer's guide */}
          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">{config.title} Buyer's Guide</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {config.considerations.map((c, i) => (
                <Card key={i} className="bg-white">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold mb-2">{c.title}</h3>
                    <p className="text-muted-foreground text-sm">{c.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* FAQs */}
          {config.faqs && config.faqs.length > 0 && (
            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">FAQs</h2>
              <Accordion type="single" collapsible className="bg-white rounded-lg border">
                {config.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="px-4">
                    <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {config.productCategorySlug && (
            <div className="text-center">
              <Button asChild variant="outline">
                <Link to={`/products/${config.productCategorySlug}`}>
                  See all {config.title} reviews & guides
                </Link>
              </Button>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

const NON_PRODUCT = /\b(axe|maul|gloves?|log store|log holder|rack|basket|moisture meter|wedge only|replacement|spare|cover|oil|carburet\w*|starter|filter)\b/i;

function isRelevant(title: string, sec: BuyersGuideSection) {
  if (!/splitter|cracker/i.test(title)) return false;
  if (NON_PRODUCT.test(title)) return false;
  if (!new RegExp(sec.mustMatch, "i").test(title)) return false;
  if (sec.exclude && new RegExp(sec.exclude, "i").test(title)) return false;
  return true;
}

function ProductGrid({ items, isLoading, reviewFor }: { items: AmazonItem[]; isLoading: boolean; reviewFor?: (asin: string) => string | undefined }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-96 rounded-lg" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="text-muted-foreground">Live picks are temporarily unavailable. Please check back shortly.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it, i) => (
        <Card key={it.asin} className="flex flex-col bg-white overflow-hidden">
          <div className="relative bg-muted/30 aspect-square flex items-center justify-center p-4">
            {it.image ? (
              <img src={it.image} alt={it.title} loading="lazy" className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="text-sm text-muted-foreground">No image</div>
            )}
            <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs">#{i + 1}</Badge>
          </div>
          <div className="p-4 flex-1 flex flex-col">
            {it.brand && <div className="text-xs text-muted-foreground uppercase mb-1">{it.brand}</div>}
            <h3 className="text-sm font-semibold line-clamp-3 mb-2">{it.title}</h3>
            {it.rating != null && (
              <div className="flex items-center gap-1 text-sm mb-2">
                <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                <span>{Number(it.rating).toFixed(1)}</span>
                {it.review_count ? <span className="text-muted-foreground">({it.review_count.toLocaleString()})</span> : null}
              </div>
            )}
            {it.price_display && <div className="text-xl font-bold mb-3">{it.price_display}</div>}
            <div className="mt-auto space-y-2">
            {reviewFor?.(it.asin) && (
              <Button asChild variant="outline" className="w-full" size="sm">
                <Link to={`/articles/${reviewFor(it.asin)}`}>Read our review</Link>
              </Button>
            )}
            <Button asChild className="w-full" size="sm">
              <a href={it.detail_url} target="_blank" rel="noopener noreferrer sponsored">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Check on Amazon
                <ExternalLink className="h-4 w-4 ml-1" />
              </a>
            </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default AmazonBuyersGuide;
